// ResetPassword.tsx
// Page de définition d'un nouveau mot de passe après réinitialisation.
// Accédée via le lien envoyé par email par Supabase (token dans l'URL).
// Updated: remplacement des champs Input mot de passe par PasswordInput avec toggle Eye/EyeOff.
// Updated: wait for authLoading before rendering invalid-link view to avoid flash.
// Updated: ajout PasswordStrengthIndicator, validation inline mismatch temps réel, seuil min 8 caractères.
// Updated: erreur de confirmation dérivée de mismatch (état courant) au lieu d'un état stale séparé.
// Updated: détection des utilisateurs invités (invitation_status = 'pending') pour afficher
//   un écran de bienvenue différencié. Après activation, marque le compte comme 'accepted'.
// Updated: markInvitationAccepted now returns a boolean; setDone(true) is only called
//   when the DB write confirms success, preventing redirect on a failed status update.
// Updated: flow-aware invalid-link message via ?type=invite query param; isInvitedUser
//   recomputed inside handleSubmit to guard against stale closure when authLoading was
//   still true during the initial render.
// Updated: wasInvitedUser state captures pre-update invitation status so the success view
//   reflects the correct message after markInvitationAccepted() mutates the auth context.
// Updated 2026-04-07: remplacement des titres texte "Qollect" par le logo SVG.
// Updated 2026-04-14: ajout des champs prénom et nom (facultatifs) pour les utilisateurs invités.
//   Ces champs sont affichés avant les champs mot de passe et enregistrés en base avant activation.
// Updated 2026-04-14: la réponse de supabase.from('users').update() est désormais vérifiée ;
//   si l'update échoue, une erreur est affichée et markInvitationAccepted() n'est pas appelé.

import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PasswordInput from '../components/ui/PasswordInput';
import PasswordStrengthIndicator from '../components/ui/PasswordStrengthIndicator';
import Card from '../components/ui/Card';

export default function ResetPassword() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [wasInvitedUser, setWasInvitedUser] = useState(false);
  const { updatePassword, markInvitationAccepted, refreshProfile, session, loading: authLoading, userProfile, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isInvitedUser = userProfile?.invitationStatus === 'pending';
  const isInviteFlow = searchParams.get('type') === 'invite';

  const isWeak = password.length > 0 && password.length < 8;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  useEffect(() => {
    if (done) {
      const timer = setTimeout(() => navigate('/dashboard'), 2500);
      return () => clearTimeout(timer);
    }
  }, [done, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    const { error: updateError } = await updatePassword(password);

    if (updateError) {
      setError('Une erreur est survenue. Veuillez réessayer ou demander un nouveau lien.');
      setLoading(false);
      return;
    }

    const currentIsInvitedUser = !authLoading && userProfile?.invitationStatus === 'pending';
    if (currentIsInvitedUser) {
      setWasInvitedUser(true);

      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();
      if (trimmedFirst || trimmedLast) {
        const { error: profileError } = await supabase
          .from('users')
          .update({ first_name: trimmedFirst || null, last_name: trimmedLast || null })
          .eq('id', user!.id);
        if (profileError) {
          console.error('ResetPassword: profile update failed', profileError);
          setError('Impossible d\'enregistrer votre profil. Veuillez réessayer.');
          setLoading(false);
          return;
        }
      }

      const accepted = await markInvitationAccepted();
      if (!accepted) {
        setError('Impossible d\'activer votre compte. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      await refreshProfile();
    }

    setDone(true);
    setLoading(false);
  };

  if (authLoading) return null;

  if (!session && !done) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-soft-xl">
          <div className="text-center mb-10">
            <img src="/logo-qollect.svg" alt="Qollect" className="h-[50px] w-auto mx-auto mb-4" />
            <p className="text-neutral-500 text-lg">Lien invalide ou expiré</p>
          </div>
          <p className="text-sm text-neutral-500 text-center mb-6 leading-relaxed">
            {isInviteFlow
              ? "Ce lien d'invitation est invalide ou a expiré. Veuillez contacter votre administrateur pour recevoir un nouveau lien d'invitation."
              : "Ce lien de réinitialisation est invalide ou a expiré. Vous pouvez en demander un nouveau ci-dessous."}
          </p>
          <Link to="/login">
            <Button fullWidth size="md">
              Retour à la connexion
            </Button>
          </Link>
          {!isInviteFlow && (
            <div className="mt-6 text-center">
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                Demander un nouveau lien
              </Link>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-soft-xl">
        <div className="text-center mb-10">
          <img src="/logo-qollect.svg" alt="Qollect" className="h-[50px] w-auto mx-auto mb-4" />
          <p className="text-neutral-500 text-lg">
            {done
              ? 'Mot de passe créé'
              : isInvitedUser
              ? 'Bienvenue'
              : 'Nouveau mot de passe'}
          </p>
        </div>

        {done ? (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-success-50 rounded-2xl flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-success-600" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-neutral-700 font-medium">
                {wasInvitedUser
                  ? 'Votre compte est maintenant actif'
                  : 'Votre mot de passe a été mis à jour'}
              </p>
              <p className="text-sm text-neutral-500">
                Vous allez être redirigé vers votre tableau de bord.
              </p>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-500 mb-6 text-center leading-relaxed">
              {isInvitedUser
                ? 'Complétez votre profil et créez votre mot de passe pour accéder à votre espace de travail.'
                : 'Choisissez un nouveau mot de passe sécurisé pour votre compte.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl font-medium">
                  {error}
                </div>
              )}

              {isInvitedUser && (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Prénom"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    placeholder="Prénom"
                  />
                  <Input
                    label="Nom"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    placeholder="Nom"
                  />
                </div>
              )}

              <div>
                <PasswordInput
                  label={isInvitedUser ? 'Créer un mot de passe' : 'Nouveau mot de passe'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <PasswordStrengthIndicator password={password} />
                {isWeak && (
                  <p className="mt-1.5 text-xs text-error-600 font-medium">
                    Le mot de passe doit contenir au moins 8 caractères
                  </p>
                )}
              </div>

              <PasswordInput
                label="Confirmer le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                error={mismatch ? 'Les mots de passe ne correspondent pas' : undefined}
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={loading}
              >
                {isInvitedUser ? 'Activer mon compte' : 'Enregistrer le nouveau mot de passe'}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
